import { AsyncPipe, NgClass } from "@angular/common";
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from "@angular/core";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { IconChevronRightComponent } from "@optee/icons/icon-chevron-right.component";
import { ButtonComponent } from "@optee/ui/components/button/button.component";
import { CircleComponent } from "@optee/ui/components/circle/circle.component";
import { IconComponent } from "@optee/ui/components/icon/icon.component";
import { CapitalizePipe } from "@optee/ui/pipes/capitalize.pipe";
import { filter, map, startWith } from "rxjs";
import { HeaderComponent } from "../layout/navigation/header/header.component";

interface Image {
  class: string;
  alt: string;
  src: string;
}
@Component({
  selector: "swc-hero-section-horizontal",
  template: `
    <div
      class="relative flex flex-col gap-10 overflow-hidden pb-8 lg:pb-16"
      [ngClass]="{
        'bg-primary-700 text-white': theme() === 'dark',
        'lg:items-start': twoImg() || multipleImg(),
        'lg:items-center': !twoImg() && !multipleImg(),
      }"
    >
      <oui-circle
        class="-left-[519px] -top-[64px] w-[1071px]"
        [theme]="theme()"
      />

      <swc-header class="z-[100]" [theme]="theme()" />

      <div
        class="z-10 flex flex-col items-center gap-12 px-8 text-center lg:flex-row lg:items-start lg:px-12 lg:text-start"
        [ngClass]="{
          'mx-auto w-11/12 justify-center': !twoImg() && !multipleImg(),
        }"
      >
        <div
          class="flex flex-col items-center justify-center gap-6 lg:items-start"
          [ngClass]="{
            'max-w-[820px] lg:py-8 lg:pl-[min(120px,5vw)] lg:pr-0':
              twoImg() || multipleImg(),
          }"
        >
          @if (currentUri$ | async; as currentUri) {
            @if (showBreadcrumbs()) {
              <nav class="flex" aria-label="Breadcrumb">
                <ul
                  class="ml-0 flex list-none items-center space-x-2"
                  role="list"
                >
                  <li>
                    <div>
                      <a
                        class="hover:text-primary-400 ml-4 text-sm font-medium text-gray-300 underline underline-offset-2"
                        routerLink="/"
                      >
                        Optee
                      </a>
                    </div>
                  </li>
                  <li>
                    <div class="flex items-center">
                      <icon-chevron-right class="size-3" aria-hidden="true" />
                      <a
                        class="hover:text-primary-400 ml-4 text-sm font-medium text-gray-300 underline underline-offset-2"
                        [routerLink]="'/' + breadcrumbParent()"
                      >
                        {{ breadcrumbParent() | capitalize }}
                      </a>
                    </div>
                  </li>
                  <li>
                    <div class="flex items-center">
                      <icon-chevron-right class="size-3" aria-hidden="true" />
                      <div
                        class="ml-4 text-sm font-medium text-gray-300"
                        aria-current="page"
                      >
                        {{ currentUri }}
                      </div>
                    </div>
                  </li>
                </ul>
              </nav>
            }
            @if (showCurrentPage()) {
              <div class="flex items-center gap-3">
                <oui-icon
                  class="text-primary-500 bg-primary-200 size-10 rounded-lg p-2"
                  [svg]="'bicolor/' + currentUri"
                />
                <div
                  class="font-medium text-gray-300"
                  aria-current="page"
                  [ngClass]="
                    currentUri === 'gtb' || currentUri === 'cvc'
                      ? 'uppercase'
                      : 'capitalize'
                  "
                >
                  @if (currentUri !== "audit") {
                    {{ currentUri }}
                  } @else {
                    Audit énergétique
                  }
                </div>
              </div>
            }
          }

          <h1
            class="max-w-[648px] text-[40px] font-semibold leading-tight lg:text-[50px]"
          >
            <ng-content select="[title]" />
          </h1>

          <div
            class="w-full max-w-[480px] font-medium {{
              theme() === 'dark' ? 'text-white' : 'text-gray-600'
            }}"
          >
            <ng-content select="[text]" />
          </div>

          @if (cta()) {
            <oui-button
              href="/demo"
              [variant]="theme() === 'dark' ? 'standard' : 'primary'"
            >
              {{ cta() }}
            </oui-button>
          }
        </div>

        @if (multipleImg().length > 0) {
          @for (image of multipleImg(); track image.src) {
            <!-- pointer-events-none  -->
            <img
              class="absolute hidden lg:flex"
              [alt]="image.alt"
              [class]="image.class"
              [src]="image.src"
            />
          }
        } @else if (twoImg()) {
          <img
            class="pointer-events-none absolute bottom-4 left-4 z-0 hidden w-3/12 sm:flex lg:bottom-10 lg:left-auto lg:right-[400px] lg:w-[275px]"
            alt="illustration devis envoyé"
            [src]="littleImgPath()"
          />

          <img
            class="pointer-events-none absolute bottom-0 right-0 -z-10 hidden w-3/12 sm:flex lg:w-[484px]"
            alt="illustration dashboard"
            fetchpriority="high"
            [src]="bigImgPath()"
          />
        } @else {
          <img
            class="w-[400px] self-center rounded-3xl"
            alt="Photo d'illustration"
            [src]="singleImgPath()"
          />
        }
      </div>
    </div>
  `,
  imports: [
    AsyncPipe,
    RouterModule,
    IconChevronRightComponent,
    HeaderComponent,
    NgClass,
    ButtonComponent,
    CircleComponent,
    IconComponent,
    CapitalizePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroSectionHorizontalComponent {
  protected readonly router = inject(Router);

  showBreadcrumbs = input(false, { transform: booleanAttribute });
  breadcrumbParent = input("clients");
  showCurrentPage = input(false, { transform: booleanAttribute });
  twoImg = input(false, { transform: booleanAttribute });
  theme = input<"light" | "dark">("dark");
  cta = input("");
  singleImgPath = input<string>("assets/cloudless-blue-sky-city.png");
  littleImgPath = input<string>("assets/hero-section/quote_sent.png");
  bigImgPath = input<string>("assets/hero-section/dashboard_2.png");
  multipleImg = input<Image[]>([]);

  currentUri$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event: NavigationEnd) => {
      const segments = event.urlAfterRedirects.split("/");
      return segments.pop() || "";
    }),
    startWith(this.router.url.split("/").pop() || ""),
  );
}
